/**
 * @file MediaUpload.tsx
 *
 * Gallery image section: composes the shared {@link MediaDropZone} (image mode)
 * with {@link ImagePreviewGrid}, shows the image count against the cap, and
 * surfaces upload failures plus drop-zone validation errors (mapped here from
 * stable codes to localized messages). Controlled — the URL list and in-flight
 * count come from the form's media hook.
 */

import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import {
  MediaDropZone,
  type MediaErrorCode,
} from '../../../components/MediaDropZone';
import { ImagePreviewGrid } from '../../../components/ImagePreviewGrid';
import {
  MAX_MEDIA_FILE_SIZE_MB,
  MAX_PRODUCT_MEDIA_IMAGES,
} from '../product-form-constants';

interface MediaUploadProps {
  imageUrls: string[];
  onImagesChange: (urls: string[]) => void;
  onImagesSelected: (files: File[]) => void;
  uploadingCount: number;
  /** Upload-failure messages from the media hook. */
  uploadErrors: string[];
  /** Opens the media library; when set, the drop zone shows a library link. */
  onSelectFromLibrary?: () => void;
}

/**
 * Renders the product image gallery uploader.
 *
 * @param props - Image URLs, change/select handlers, in-flight count, errors.
 *
 * @returns The media section.
 */
export function MediaUpload({
  imageUrls,
  onImagesChange,
  onImagesSelected,
  uploadingCount,
  uploadErrors,
  onSelectFromLibrary,
}: MediaUploadProps) {
  const intl = useIntl();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const codeToMessage = (code: MediaErrorCode): string => {
    if (code === 'type') {
      return intl.formatMessage(adminMessages.formErrorUnsupportedType);
    }
    if (code === 'size') {
      return intl.formatMessage(adminMessages.formErrorFileTooLarge, {
        maxMb: MAX_MEDIA_FILE_SIZE_MB,
      });
    }
    return intl.formatMessage(adminMessages.formErrorImageLimit, {
      max: MAX_PRODUCT_MEDIA_IMAGES,
    });
  };

  const remaining = MAX_PRODUCT_MEDIA_IMAGES - imageUrls.length - uploadingCount;
  const errors = [...validationErrors, ...uploadErrors];

  return (
    <div className="gv-flex gv-flex-col gv-gap-md gv-w-full">
      {remaining > 0 && (
        <MediaDropZone
          testId="product-form-media"
          onFilesSelected={(files) => {
            setValidationErrors([]);
            onImagesSelected(files);
          }}
          onError={(codes) => setValidationErrors(codes.map(codeToMessage))}
          maxFileSizeMb={MAX_MEDIA_FILE_SIZE_MB}
          remainingSlots={remaining}
          title={intl.formatMessage(adminMessages.formMediaDropTitle)}
          browseLabel={intl.formatMessage(adminMessages.formUploadFromComputer)}
          hint={intl.formatMessage(adminMessages.formMediaHint, {
            maxMb: MAX_MEDIA_FILE_SIZE_MB,
          })}
          onSelectFromLibrary={onSelectFromLibrary}
          libraryLabel={intl.formatMessage(adminMessages.formSelectFromLibrary)}
          orLabel={intl.formatMessage(adminMessages.formOr)}
        />
      )}

      {imageUrls.length > 0 || uploadingCount > 0 ? (
        <ImagePreviewGrid
          files={imageUrls}
          onFilesChange={onImagesChange}
          uploadingCount={uploadingCount}
          altText={intl.formatMessage(adminMessages.formMedia)}
          uploadingLabel={intl.formatMessage(adminMessages.formUploading)}
          featuredLabel={intl.formatMessage(adminMessages.formFeaturedImage)}
          actionsLabel={intl.formatMessage(adminMessages.formImageActions)}
          closeLabel={intl.formatMessage(adminMessages.productsClose)}
          setFeaturedLabel={intl.formatMessage(adminMessages.formSetFeatured)}
          removeFeaturedLabel={intl.formatMessage(adminMessages.formRemoveFeatured)}
          moveUpLabel={intl.formatMessage(adminMessages.formMoveUpOrder)}
          moveDownLabel={intl.formatMessage(adminMessages.formMoveDownOrder)}
          removeLabel={intl.formatMessage(adminMessages.formRemoveFromSelection)}
        />
      ) : null}

      <p className="gv-caption-lg gv-text-on-alternative">
        <FormattedMessage
          {...adminMessages.formMediaCount}
          values={{ count: imageUrls.length, max: MAX_PRODUCT_MEDIA_IMAGES }}
        />
      </p>

      {errors.map((message, index) => (
        <p key={`${message}-${index}`} className="gv-text-sm gv-state-critical" role="alert">
          {message}
        </p>
      ))}
    </div>
  );
}
